import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useConfig } from "../lib/config";
import { toast } from "sonner";
import { Upload, FileText, Loader2, Check, Infinity as InfinityIcon, RefreshCw, Calendar } from "lucide-react";

export default function ConvenioNew() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { config } = useConfig();
  const [pdf, setPdf] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [facultades, setFacultades] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    institucion_nombre: "",
    institucion_pais: "",
    institucion_ciudad: "",
    institucion_tipo: "universidad",
    tipo: "marco",
    vigencia_tipo: "fijo",
    fecha_firma: "",
    fecha_vencimiento: "",
    aviso_previo_dias: 30,
    facultad_ids: [],
    unidad_ids: [],
    estructura_ids: [],
    areas_tematicas: "",
    observaciones: "",
  });

  const [estructuras, setEstructuras] = useState([]);

  useEffect(() => {
    api.get("/facultades").then((r) => setFacultades(r.data));
    api.get("/estructuras").then((r) => setEstructuras(r.data)).catch(() => {});
    if (config.enable_unidades) {
      api.get("/unidades").then((r) => setUnidades(r.data));
    }
  }, [config.enable_unidades]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleFacultad = (id) => {
    setForm((f) => ({
      ...f,
      facultad_ids: f.facultad_ids.includes(id)
        ? f.facultad_ids.filter((x) => x !== id)
        : [...f.facultad_ids, id],
    }));
  };
  const toggleUnidad = (id) => {
    setForm((f) => ({
      ...f,
      unidad_ids: f.unidad_ids.includes(id)
        ? f.unidad_ids.filter((x) => x !== id)
        : [...f.unidad_ids, id],
    }));
  };
  const toggleEstructura = (id) => {
    setForm((f) => ({
      ...f,
      estructura_ids: f.estructura_ids.includes(id)
        ? f.estructura_ids.filter((x) => x !== id)
        : [...f.estructura_ids, id],
    }));
  };

  const handlePdfChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error(t("cn.must_pdf"));
      return;
    }
    setPdf(file);
    setExtracting(true);
    setExtracted(null);
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      const { data } = await api.post("/convenios/extract-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExtracted(data);
      setForm((f) => ({
        ...f,
        institucion_nombre: data.institucion_nombre || f.institucion_nombre,
        institucion_pais: data.institucion_pais || f.institucion_pais,
        institucion_ciudad: data.institucion_ciudad || f.institucion_ciudad,
        tipo: data.tipo || f.tipo,
        fecha_firma: data.fecha_firma || f.fecha_firma,
        fecha_vencimiento: data.fecha_vencimiento || f.fecha_vencimiento,
        areas_tematicas: (data.areas_tematicas || []).join(", "),
        observaciones: data.observaciones || f.observaciones,
      }));
      toast.success(t("cn.ai_extract_done"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("cn.ai_extract_err"));
    } finally {
      setExtracting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        areas_tematicas: form.areas_tematicas
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      if (form.vigencia_tipo === "indefinido") {
        payload.fecha_vencimiento = null;
        payload.aviso_previo_dias = null;
      } else if (form.vigencia_tipo === "fijo") {
        payload.aviso_previo_dias = null;
      } else if (form.vigencia_tipo === "auto_renovable") {
        payload.aviso_previo_dias = Number(form.aviso_previo_dias) || 30;
      }
      const { data } = await api.post("/convenios", payload);
      toast.success(t("cn.created"));
      nav(`/convenios/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_save"));
    } finally {
      setSaving(false);
    }
  };

  const isAI = (field) => extracted && extracted[field];

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="mb-8 sm:mb-10">
          <div className="section-marker">{t("cn.kicker")}</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
            {t("cn.h1_a")} <span className="serif-em">{t("cn.h1_em")}</span>
          </h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">{t("cn.desc")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 sm:gap-10">
          {/* Left: PDF zone */}
          <div className="lg:col-span-2">
            <div className="section-marker mb-3">{t("cn.step1")}</div>
            {!pdf ? (
              <label className="border-2 border-dashed border-zinc-300 p-10 flex flex-col items-center justify-center cursor-pointer hover:border-zinc-950 hover:bg-zinc-50 transition-colors min-h-[300px]" data-testid="pdf-dropzone">
                <Upload size={48} strokeWidth={1} className="text-zinc-400 mb-4" />
                <div className="font-medium">{t("cn.pdf_drop")}</div>
                <div className="text-sm text-zinc-500 mt-2 text-center max-w-xs">{t("cn.pdf_hint")}</div>
                <input type="file" accept="application/pdf" onChange={handlePdfChange} className="hidden" data-testid="pdf-input" />
              </label>
            ) : (
              <div className="border border-zinc-200 p-6">
                <div className="flex items-start gap-4">
                  <FileText size={36} strokeWidth={1} className="text-zinc-950 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{pdf.name}</div>
                    <div className="text-sm text-zinc-500">{(pdf.size / 1024).toFixed(0)} KB</div>
                  </div>
                </div>
                {extracting && (
                  <div className="mt-6 p-4 bg-zinc-50 border border-zinc-200 flex items-center gap-3">
                    <Loader2 size={18} className="animate-spin" />
                    <div>
                      <div className="text-sm font-medium">{t("cn.processing")}</div>
                      <div className="text-xs text-zinc-500">{t("cn.processing_sub")}</div>
                    </div>
                  </div>
                )}
                {extracted && !extracting && (
                  <div className="mt-6 p-4 bg-zinc-50 border border-zinc-950">
                    <div className="text-sm font-medium ai-diamond">{t("cn.ai_done")}</div>
                    <div className="text-xs text-zinc-600 mt-1">
                      {t("cn.ai_conf")}: <strong className="capitalize">{extracted.extraction_confidence || "media"}</strong>
                    </div>
                    {extracted.extraction_notes && (
                      <div className="text-xs text-zinc-500 mt-2">{extracted.extraction_notes}</div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => {
                    setPdf(null);
                    setExtracted(null);
                  }}
                  className="mt-4 text-sm underline text-zinc-600"
                  data-testid="replace-pdf"
                >
                  {t("cn.change_file")}
                </button>
              </div>
            )}

            <div className="mt-6 p-4 border border-zinc-200">
              <div className="text-xs text-zinc-500">{t("cn.scanned_hint")}</div>
            </div>
          </div>

          {/* Right: form */}
          <form onSubmit={submit} className="lg:col-span-3 space-y-5" data-testid="convenio-form">
            <div className="section-marker mb-1">{t("cn.step2")}</div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                {t("cn.institucion")} {isAI("institucion_nombre") && <span className="text-zinc-950 normal-case">◆ IA</span>}
              </label>
              <input required value={form.institucion_nombre} onChange={set("institucion_nombre")} className="w-full border border-zinc-300 px-4 py-3" data-testid="cv-institucion" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {t("cn.pais")} {isAI("institucion_pais") && <span className="text-zinc-950 normal-case">◆</span>}
                </label>
                <input required value={form.institucion_pais} onChange={set("institucion_pais")} className="w-full border border-zinc-300 px-4 py-3" data-testid="cv-pais" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("cn.ciudad")}</label>
                <input value={form.institucion_ciudad} onChange={set("institucion_ciudad")} className="w-full border border-zinc-300 px-4 py-3" data-testid="cv-ciudad" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {t("cn.tipo")} {isAI("tipo") && <span className="text-zinc-950 normal-case">◆</span>}
                </label>
                <select value={form.tipo} onChange={set("tipo")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="cv-tipo">
                  <option value="marco">{t("cn.tipo_marco")}</option>
                  <option value="especifico">{t("cn.tipo_esp")}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("cn.tipo_inst")}</label>
                <select value={form.institucion_tipo} onChange={set("institucion_tipo")} className="w-full border border-zinc-300 px-4 py-3 bg-white">
                  <option value="universidad">{t("cn.inst_universidad")}</option>
                  <option value="empresa">{t("cn.inst_empresa")}</option>
                  <option value="gobierno">{t("cn.inst_gobierno")}</option>
                  <option value="ong">{t("cn.inst_ong")}</option>
                  <option value="otro">{t("cn.inst_otro")}</option>
                </select>
              </div>
            </div>

            {/* Vigencia selector */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">{t("cn.vigencia_label")}</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { v: "fijo", icon: Calendar, label: t("cn.vig_fija"), desc: t("cn.vig_fija_d") },
                  { v: "indefinido", icon: InfinityIcon, label: t("cn.vig_indef"), desc: t("cn.vig_indef_d") },
                  { v: "auto_renovable", icon: RefreshCw, label: t("cn.vig_auto"), desc: t("cn.vig_auto_d") },
                ].map(({ v, icon: Icon, label, desc }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, vigencia_tipo: v }))}
                    className={`border p-4 text-left transition-colors ${form.vigencia_tipo === v ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:bg-zinc-50"}`}
                    data-testid={`cv-vig-${v}`}
                  >
                    <Icon size={18} strokeWidth={1.5} className="mb-2" />
                    <div className="font-medium text-sm">{label}</div>
                    <div className="text-xs text-zinc-500 mt-1">{desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {t("cn.fecha_firma")} {isAI("fecha_firma") && <span className="text-zinc-950 normal-case">◆</span>}
                </label>
                <input required type="date" value={form.fecha_firma} onChange={set("fecha_firma")} className="w-full border border-zinc-300 px-4 py-3" data-testid="cv-firma" />
              </div>
              {form.vigencia_tipo !== "indefinido" && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                    {form.vigencia_tipo === "auto_renovable" ? t("cn.fecha_1er") : t("cn.fecha_vencimiento")}
                    {isAI("fecha_vencimiento") && <span className="text-zinc-950 normal-case"> ◆</span>}
                  </label>
                  <input required type="date" value={form.fecha_vencimiento} onChange={set("fecha_vencimiento")} className="w-full border border-zinc-300 px-4 py-3" data-testid="cv-vencimiento" />
                </div>
              )}
            </div>

            {form.vigencia_tipo === "auto_renovable" && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {t("cn.aviso")}
                </label>
                <input type="number" min="1" max="365" required value={form.aviso_previo_dias} onChange={set("aviso_previo_dias")} className="border border-zinc-300 px-4 py-3 w-32 tabular-nums" data-testid="cv-aviso" />
                <p className="text-xs text-zinc-500 mt-2">{t("cn.aviso_hint")}</p>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                {t("cn.facultades_assoc", { plural: config.facultad_label_plural || t("nav.facultades") })}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {facultades.map((f) => (
                  <label key={f.id} className={`border px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${form.facultad_ids.includes(f.id) ? "border-zinc-950 bg-zinc-50" : "border-zinc-200"}`} data-testid={`cv-fac-${f.codigo}`}>
                    <input type="checkbox" checked={form.facultad_ids.includes(f.id)} onChange={() => toggleFacultad(f.id)} className="accent-zinc-950" />
                    {f.nombre}
                  </label>
                ))}
              </div>
            </div>

            {config.enable_unidades && unidades.length > 0 && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {t("cn.unidades_assoc", { plural: config.unidad_label_plural || t("nav.unidades") })}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {unidades.map((u) => (
                    <label key={u.id} className={`border px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${form.unidad_ids.includes(u.id) ? "border-zinc-950 bg-zinc-50" : "border-zinc-200"}`} data-testid={`cv-uni-${u.codigo}`}>
                      <input type="checkbox" checked={form.unidad_ids.includes(u.id)} onChange={() => toggleUnidad(u.id)} className="accent-zinc-950" />
                      <span className="truncate">{u.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {estructuras.length > 0 && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {t("cn.estructuras_assoc")}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {estructuras.map((e) => (
                    <label key={e.id} className={`border px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${form.estructura_ids.includes(e.id) ? "border-zinc-950 bg-zinc-50" : "border-zinc-200"}`} data-testid={`cv-est-${e.id}`}>
                      <input type="checkbox" checked={form.estructura_ids.includes(e.id)} onChange={() => toggleEstructura(e.id)} className="accent-zinc-950" />
                      <span className="truncate">{e.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("cn.areas")}</label>
              <input value={form.areas_tematicas} onChange={set("areas_tematicas")} placeholder={t("cn.areas_ph")} className="w-full border border-zinc-300 px-4 py-3" data-testid="cv-areas" />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("cn.observaciones")}</label>
              <textarea rows={3} value={form.observaciones} onChange={set("observaciones")} className="w-full border border-zinc-300 px-4 py-3 resize-none" data-testid="cv-obs" />
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => nav(-1)} className="border border-zinc-300 px-6 py-3 font-medium hover:bg-zinc-50">{t("common.cancel")}</button>
              <button type="submit" disabled={saving} className="flex-1 bg-zinc-950 text-white px-6 py-3 font-medium hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="cv-submit">
                {saving ? t("cn.submit_saving") : (<><Check size={18} strokeWidth={1.5} /> {t("cn.submit_create")}</>)}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
